import React, { useState } from 'react';
import { CommonWrapper } from 'screens/AgentOfficePages/styled';
import { TableFiltersWrapper } from 'components/widget/prices/section/listings/styled';
import { FiltersWrapper } from 'components/navigation/subheader/styled';
import PricesSummaryFilters from './Filters';
import { DealsTable } from './Table';
import { MarketplaceType, IAgentOfficeDeal } from 'utils/entities';
import { ValuesTuple } from 'ds/components/slider/utils';
import { PropertyType } from 'components/filters/types';
import { config as filtersConfig } from 'components/filters/config';
import { flow } from 'lodash';
import { UiAgentOfficeDeal } from 'screens/AgentOfficePages/common/sections/deals/types';
import { countDealsTotal } from 'screens/AgentOfficePages/common/sections/deals/helpers';

export interface FilterState {
  beds: ValuesTuple;
  price: ValuesTuple;
  propertyType: PropertyType[];
}

interface TableFilterParams {
  list: IAgentOfficeDeal[];
  filter?: FilterState;
}

enum DealsPropertyType {
  AgricultureLand = 'AGRICULTURE_LAND',
  SerialCottage = 'SERIAL_COTTAGE',
  DualCottage = 'DUAL_COTTAGE',
  GardenApartment = 'GARDEN_APARTMENT',
  RoofFlat = 'ROOF_FLAT',
  PenthouseApp = 'PENTHOUSE_APP',
  MiniPenthouse = 'MINI_PENTHOUSE',
  VacationProperty = 'VACATION_PROPERTY',
}

const propertyTypeToDealsPropertyTypeMap: Record<DealsPropertyType, PropertyType> = {
  [DealsPropertyType.AgricultureLand]: PropertyType.AgricultureLand,
  [DealsPropertyType.SerialCottage]: PropertyType.SerialCottage,
  [DealsPropertyType.DualCottage]: PropertyType.TwoFamilyDwelling,
  [DealsPropertyType.GardenApartment]: PropertyType.GardenApartment,
  [DealsPropertyType.RoofFlat]: PropertyType.RoofFlat,
  [DealsPropertyType.PenthouseApp]: PropertyType.PenthouseApp,
  [DealsPropertyType.MiniPenthouse]: PropertyType.MiniPenthouse,
  [DealsPropertyType.VacationProperty]: PropertyType.VacationProperty,
};

const maxPrice = filtersConfig[MarketplaceType.Commercial].budgetByDealType.unitBuy.budgetSliderProps.max;
const minPrice = filtersConfig[MarketplaceType.Commercial].budgetByDealType.unitBuy.budgetSliderProps.min;
export const DEFAULT_PRICE_VALUE: ValuesTuple = [ minPrice, maxPrice ];

export const Deals: React.FC<{ data?: UiAgentOfficeDeal[] }> = ({ data }) => {
  const [ tableData, setTableData ] = useState({ data, total: countDealsTotal(data) });
  const [ filterState, setFilterState ] = useState<FilterState>({
    beds: [ null, null ],
    price: DEFAULT_PRICE_VALUE,
    propertyType: [],
  });

  const filterByBeds = ({ list, filter = filterState }: TableFilterParams) => {
    const bedsCount = filter.beds.filter(Boolean).length;

    if (!bedsCount) return { list, filter };

    if (bedsCount === 1) {
      return { list: list.filter(deal => deal.numRooms === filter.beds[0]), filter };
    }

    if (filter.beds[1] === -1) {
      return { list: list.filter(deal => deal.numRooms >= filter.beds[0]), filter };
    }
    else {
      return { list: list.filter(deal => deal.numRooms >= filter.beds[0] && deal.numRooms <= filter.beds[1]), filter };
    }
  };

  const filterByPrice = ({ list, filter = filterState }: TableFilterParams) => {
    return ({ list: list.filter(deal => deal.declaredPrice >= filter.price[0] && (filter.price[1] === maxPrice ? true : deal.declaredPrice <= filter.price[1])), filter });
  };

  const filterByPropertyType = ({ list, filter = filterState }: TableFilterParams) => {
    const propertyTypeList = filter.propertyType.filter(Boolean);

    if (!propertyTypeList.length) return list;
    const propertyTypeSet = new Set(propertyTypeList);

    return list.filter(deal => propertyTypeSet.has(propertyTypeToDealsPropertyTypeMap[deal.propertyType] || deal.propertyType.toLocaleLowerCase()));
  };

  const tableFilter = flow([ filterByBeds, filterByPrice, filterByPropertyType ]);

  const applyFilter = () => {
    const nextData = tableFilter({ list: data });
    setTableData({ data: nextData, total: countDealsTotal(nextData) });
  };
  const applyCustomFilter = (filter: FilterState) => {
    const nextData = tableFilter({ list: data, filter });
    setTableData({ data: nextData, total: countDealsTotal(nextData) });
  };

  return (
    <CommonWrapper>
      <TableFiltersWrapper>
        <FiltersWrapper>
          <PricesSummaryFilters
            filterState={filterState}
            setFilterState={setFilterState}
            count={tableData.total}
            applyFilter={applyFilter}
            applyCustomFilter={applyCustomFilter}
          />
        </FiltersWrapper>
      </TableFiltersWrapper>
      <DealsTable data={tableData.data} />
    </CommonWrapper>
  );
};
